import React, { useState } from 'react';
import {
  X,
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  AlertCircle,
  Phone,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db, functions } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';


export default function AuthModal({ isOpen, onClose }) {

  const [authTab, setAuthTab] = useState('login'); // 'login' | 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authForm, setAuthForm] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    password: '',
  });

  if (!isOpen) return null;

  const resetForm = () => {
    setAuthForm({ fullName: '', mobileNumber: '', email: '', password: '' });
    setAuthError('');
    setShowPassword(false);
    setIsSubmitting(false);
  };

  const handleTabChange = (tab) => {
    setAuthTab(tab);
    setAuthError('');
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSubmitting(true);

    try {
      if (authTab === 'register') {
        if (!authForm.fullName.trim()) throw new Error("Full name is required");
        if (authForm.password.length < 6) throw new Error("Password must be at least 6 characters long");

        const createUser = httpsCallable(functions, 'manageUser');
        const res = await createUser({
          name: authForm.fullName.trim(),
          mobile: authForm.mobileNumber.trim(),
          email: authForm.email.trim(),
          password: authForm.password,
          action: "add"
        });
        console.log(res.data);

        // Auto login after registration
        const loginRes = await signInWithEmailAndPassword(auth, authForm.email.trim(), authForm.password);
        if (loginRes.user) {
          // Set user details in local storage for navigation bar with UID
          const sessionUser = {
            uid: loginRes.user.uid,
            fullName: authForm.fullName.trim(),
            email: authForm.email.trim(),
            mobileNumber: authForm.mobileNumber.trim(),
            isLoggedIn: true
          };
          localStorage.setItem('user', JSON.stringify(sessionUser));
          window.dispatchEvent(new Event('user-login'));

          onClose();
          resetForm();
          setAuthSuccess(true);
        }
      } else {
        const res = await signInWithEmailAndPassword(auth, authForm.email.trim(), authForm.password);
        if (res.user) {
          const uid = res.user.uid;
          const email = res.user.email;
          
          // Try to fetch custom profile from Firestore using UID first (fallback to email for legacy docs)
          let fullName = res.user.displayName;
          let userData = {};
          try {
            let userDocRef = doc(db, "users", uid);
            let docSnap = await getDoc(userDocRef);
            if (!docSnap.exists() && email) {
              userDocRef = doc(db, "users", email.toLowerCase());
              docSnap = await getDoc(userDocRef);
            }
            if (docSnap.exists()) {
              userData = docSnap.data();
              fullName = userData.fullName || (userData.firstName && userData.lastName ? (userData.firstName + " " + userData.lastName) : fullName);
            }
          } catch (err) {
            console.error("Error loading user profile:", err);
          }

          if (!fullName && email) {
            fullName = email.split('@')[0];
          }

          const sessionUser = {
            uid,
            fullName: fullName || 'User',
            email,
            ...userData,
            isLoggedIn: true
          };
          localStorage.setItem('user', JSON.stringify(sessionUser));
          window.dispatchEvent(new Event('user-login'));

          onClose();
          resetForm();
          setAuthSuccess(true);
        } else {
          setAuthError("Invalid credentials");
        }
      }
    } catch (err) {
      console.error(err);
      setAuthError(err.message || "Authentication failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      {/* Backdrop */}

      <div className="absolute inset-0 cursor-default" onClick={onClose} />

      {/* Modal Container */}
      <div className="relative w-full bg-white rounded-3xl shadow-2xl z-10 overflow-hidden transform transition-all duration-300 animate-in zoom-in-95 ease-out text-left max-w-lg">

        {/* Dark Header — same CSS classes as CartModal */}
        <div className="chefco-cart-header">
          <h2 className="chefco-cart-title">
            {authTab === 'login' ? 'Sign In' : 'Join ChefCo'}
          </h2>
          <button
            onClick={() => { onClose(); resetForm(); }}
            className="chefco-cart-close"
            aria-label="Close authentication modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {authSuccess ? (
          /* ── SUCCESS SCREEN ─────────────────────────────────── */
          <div className="flex flex-col items-center justify-center px-8 py-12 text-center animate-in fade-in duration-200">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-sm border border-emerald-100/50">
              <svg className="w-8 h-8 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-wide mb-1">
              {authTab === 'login' ? 'Welcome Back' : 'Account Created'}
            </h3>
            <p className="text-sm font-bold text-slate-400 mt-2 uppercase tracking-wide">
              {authTab === 'login' ? 'Logging you into ChefCo...' : 'Preparing your wholesale dashboard...'}
            </p>
          </div>
        ) : (
          /* ── MAIN FORMS ─────────────────────────────────────── */
          <div className="p-8">

            {/* Tab switcher */}
            <div className="flex bg-slate-50 p-1.5 rounded-2xl mb-5">
              <button
                type="button"
                onClick={() => handleTabChange('login')}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs border-none cursor-pointer transition-all ${authTab === 'login'
                  ? 'bg-[#2C3E2B] text-white shadow-md shadow-[#2C3E2B]/15'
                  : 'bg-transparent text-slate-400 hover:text-slate-700'
                  }`}
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('register')}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs border-none cursor-pointer transition-all ${authTab === 'register'
                  ? 'bg-[#2C3E2B] text-white shadow-md shadow-[#2C3E2B]/15'
                  : 'bg-transparent text-slate-400 hover:text-slate-700'
                  }`}
              >
                Sign Up
              </button>
            </div>

            {/* Inline error banner */}
            {authError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-xl px-4 py-3 mb-4 animate-in fade-in duration-150">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <form onSubmit={handleAuthSubmit} className="space-y-4">

              {/* ── REGISTER FORM ─────────────────────────────── */}
              {authTab === 'register' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider mb-1">Full Name*</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                        <input
                          type="text"
                          required
                          placeholder="Full Name"
                          value={authForm.fullName}
                          onChange={(e) => setAuthForm({ ...authForm, fullName: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                        />
                      </div>
                    </div>
                    {/* Mobile Number */}
                    <div>
                      <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider mb-1">Mobile Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                        <input
                          type="tel"
                          placeholder="Mobile Number"
                          value={authForm.mobileNumber}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setAuthForm({ ...authForm, mobileNumber: val });
                          }}
                          className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider mb-1">Email Address*</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                      <input
                        type="email"
                        required
                        placeholder="you@restaurant.com"
                        value={authForm.email}
                        onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                        className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider mb-1">Password* <span className="normal-case font-medium text-slate-600">(min 6 chars)</span></label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="••••••••"
                        value={authForm.password}
                        onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                        className="w-full pl-11 pr-11 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-700 flex items-center justify-center p-0"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}

                    className="w-full flex items-center justify-center py-3.5 bg-[#2C3E2B] hover:bg-[#006f3c] hover:text-black text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-md transition-all cursor-pointer border-none mt-2 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none group"
                    onClick={handleAuthSubmit}
                 >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white group-hover:text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              ) : (
                /* ── LOGIN FORM ─────────────────────────────────── */
                <div className="space-y-4">
                  {/* Email */}
                  <div>
                    <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                      <input
                        type="email"
                        required
                        placeholder="you@restaurant.com"
                        value={authForm.email}
                        onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                        className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-[11px] font-extrabold text-slate-900 uppercase tracking-wider">Password</label>
                      <span className="text-[10px] font-bold text-[#9E2A2B] cursor-default select-none">
                        Forgot Password?
                      </span>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="••••••••"
                        value={authForm.password}
                        onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                        className="w-full pl-11 pr-11 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#2C3E2B] focus:ring-1 focus:ring-[#2C3E2B] outline-none transition font-semibold text-slate-900 text-sm placeholder-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-700 flex items-center justify-center p-0"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="remember-me"
                      className="w-4 h-4 accent-[#2C3E2B] border-slate-200 rounded cursor-pointer"
                    />
                    <label htmlFor="remember-me" className="ml-2 text-xs font-extrabold text-slate-800 cursor-pointer select-none">
                      Remember my login session
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center py-3.5 bg-[#2C3E2B] hover:bg-[#d0d0d0] hover:text-black text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-md transition-all cursor-pointer border-none mt-2 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none group"
                    onClick={handleAuthSubmit}
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white group-hover:text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting ? 'Signing In...' : 'Sign In To Account'}
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
